<script setup lang="ts">
import {ref, computed, watch} from 'vue';
import {parsePageRanges, buildPageRangeString} from './smart-review-utils.ts';
import {FILE_DIFF_STATUS, type FileDiffStatus} from '../allspice_app/types/file_tree.ts';
import {statusIcon, statusClasses} from '../allspice_app/components/FileTreeItems/utils.ts';
import {SvgIcon} from '../svg.ts';

type PageInfo = {
  name: string;
  pageId: string;
  number: number;
  status: FileDiffStatus;
};

const props = defineProps<{
  pages: PageInfo[];
  isLoading: boolean;
  loadError: boolean;
  disabled: boolean;
  locale: {
    selectPagesHeader: string;
    pageInputPlaceholder: string;
    selectAll: string;
    loading: string;
    noPages: string;
    loadFailed: string;
    showUnchanged: string;
  };
}>();

const selectedPageIds = defineModel<Set<string>>('selectedPageIds', {required: true});

const rangeInput = ref('');
// Default to showing only changed pages (toggle off). Turning the toggle on
// reveals the unchanged pages so they can be opted into review. Selection state
// for hidden pages is preserved, so toggling visibility never loses a selection.
const showUnchanged = ref(false);

let updatingFromInput = false;
let updatingFromCheckbox = false;

function isUnchanged(page: PageInfo): boolean {
  return page.status === FILE_DIFF_STATUS.UNCHANGED;
}

// Reuse the Changes-tab diff icon mapping so changed pages render the same
// colored diff icon as the file tree; unchanged pages map to '' (no icon),
// matching the tree's "no icon = unchanged" convention.
function pageIcon(page: PageInfo): string {
  return statusIcon(page.status);
}
function pageIconClasses(page: PageInfo): string[] | string {
  return statusClasses(page.status);
}

const hasUnchanged = computed(() => props.pages.some(isUnchanged));
// The working set the list and select-all operate on. With the toggle on, all
// pages. With it off, changed pages plus any unchanged page the user has
// explicitly selected — a selected sheet must stay visible so the user can see
// (and uncheck) what's included in the run, rather than it silently lingering.
const visiblePages = computed(() =>
  showUnchanged.value ?
    props.pages :
    props.pages.filter(
      (p) => !isUnchanged(p) || selectedPageIds.value.has(p.pageId),
    ),
);

const allChecked = computed(() =>
  visiblePages.value.length > 0 &&
  visiblePages.value.every((p) => selectedPageIds.value.has(p.pageId)),
);
const noneChecked = computed(() =>
  visiblePages.value.every((p) => !selectedPageIds.value.has(p.pageId)),
);
const toggleAllState = computed(() => {
  if (allChecked.value) return 'checked';
  if (noneChecked.value) return 'unchecked';
  return 'indeterminate';
});

function toggleAll() {
  // Operate only on the visible pages, leaving any selection on hidden pages
  // untouched. (Selected pages are always visible, so this never silently
  // drops a selection; it just scopes select-all/none to what's shown.)
  const next = new Set(selectedPageIds.value);
  if (allChecked.value) {
    for (const p of visiblePages.value) next.delete(p.pageId);
  } else {
    for (const p of visiblePages.value) next.add(p.pageId);
  }
  selectedPageIds.value = next;
  syncInputFromCheckboxes(next);
}

function onCheckboxToggle(page: PageInfo, checked: boolean) {
  const next = new Set(selectedPageIds.value);
  if (checked) {
    next.add(page.pageId);
  } else {
    next.delete(page.pageId);
  }
  selectedPageIds.value = next;
  if (!updatingFromInput) syncInputFromCheckboxes(next);
}

// Range numbers are real sheet numbers across all pages, so a range can
// select any sheet regardless of the show-unchanged view filter.
function pageIdsInRange(input: string): Set<string> {
  const selectedNumbers = parsePageRanges(input, props.pages.length);
  const ids = new Set<string>();
  for (const p of props.pages) {
    if (selectedNumbers.has(p.number)) ids.add(p.pageId);
  }
  return ids;
}

function onRangeInput() {
  if (updatingFromCheckbox) return;
  updatingFromInput = true;
  selectedPageIds.value = pageIdsInRange(rangeInput.value);
  updatingFromInput = false;
}

function syncInputFromCheckboxes(selected: Set<string>) {
  updatingFromCheckbox = true;
  const checkedNumbers = props.pages
    .filter((p) => selected.has(p.pageId))
    .map((p) => p.number);
  if (checkedNumbers.length === props.pages.length || checkedNumbers.length === 0) {
    rangeInput.value = '';
  } else {
    rangeInput.value = buildPageRangeString(checkedNumbers);
  }
  updatingFromCheckbox = false;
}

watch(() => props.isLoading, (isLoading, wasLoading) => {
  if (wasLoading && !isLoading && props.pages.length > 0) {
    syncInputFromCheckboxes(selectedPageIds.value);
  }
});

// The parent can replace the selection too (Resume narrows it to the pages not
// yet reviewed). Rewrite the range only when it no longer describes the
// selection, so a range the user is typing is left alone.
watch(selectedPageIds, (selected) => {
  const typed = pageIdsInRange(rangeInput.value);
  if (typed.size !== selected.size || Array.from(typed).some((id) => !selected.has(id))) {
    syncInputFromCheckboxes(selected);
  }
});
</script>

<template>
  <div class="tw:font-semibold tw:mb-2">{{ locale.selectPagesHeader }}</div>

  <div class="tw:mb-2">
    <div class="ui small fluid input">
      <input
        type="text"
        :placeholder="locale.pageInputPlaceholder"
        v-model="rangeInput"
        :disabled="disabled"
        @input="onRangeInput"
      >
    </div>
  </div>

  <div class="tw:flex tw:items-center tw:gap-2 tw:py-2 smart-review-toggle-all-row">
    <input
      type="checkbox"
      :checked="allChecked"
      :indeterminate.prop="toggleAllState === 'indeterminate'"
      :disabled="disabled || visiblePages.length === 0"
      @change="toggleAll"
    >
    <span class="tw:text-sm tw:text-grey">{{ locale.selectAll }}</span>
    <label
      v-if="hasUnchanged"
      class="tw:flex tw:items-center tw:gap-1 tw:ml-auto tw:text-sm tw:text-grey tw:cursor-pointer"
    >
      <input type="checkbox" v-model="showUnchanged" :disabled="disabled">
      {{ locale.showUnchanged }}
    </label>
  </div>

  <div class="smart-review-page-list">
    <div v-if="isLoading" class="tw:flex tw:items-center tw:justify-center tw:gap-2 tw:py-4 tw:text-grey smart-review-loading">
      {{ locale.loading }}
    </div>
    <div v-else-if="loadError" class="ui red text tw:my-2">
      {{ locale.loadFailed }}
    </div>
    <div v-else-if="pages.length === 0 && !isLoading" class="ui grey text tw:my-2">
      {{ locale.noPages }}
    </div>
    <template v-else>
      <label
        v-for="page in visiblePages"
        :key="page.pageId || page.name"
        class="tw:flex tw:items-center tw:gap-2 tw:py-1 tw:cursor-pointer"
      >
        <input
          type="checkbox"
          :checked="selectedPageIds.has(page.pageId)"
          :disabled="disabled"
          @change="(e) => onCheckboxToggle(page, (e.target as HTMLInputElement).checked)"
        >
        <span class="tw:text-grey tw:text-sm tw:text-right smart-review-page-number">{{ page.number }}.</span>
        <span class="smart-review-page-name" :title="page.name">{{ page.name }}</span>
        <SvgIcon
          v-if="pageIcon(page)"
          :name="pageIcon(page)"
          :class="pageIconClasses(page)"
          :size="14"
          class="smart-review-page-status"
        />
      </label>
    </template>
  </div>
</template>

<style scoped>
.smart-review-toggle-all-row {
  border-bottom: 1px solid var(--color-secondary);
}

.smart-review-page-list {
  max-height: 180px;
  overflow-y: auto;
  /* Right padding keeps the right-aligned status icon clear of the scrollbar;
     reserve a stable gutter so it doesn't shift when the scrollbar appears. */
  padding: 4px 10px 4px 0;
  scrollbar-gutter: stable;
}

/* Fixed-ish number column: wide enough for 2-3 digit sheet numbers (plus the
   trailing ".") so it never wraps, and never shrinks/grows with the row. */
.smart-review-page-number {
  flex: none;
  min-width: 2.25rem;
}

/* The name takes the remaining width and truncates with an ellipsis instead of
   wrapping; the full name is available via the title tooltip. min-width:0 is
   required for a flex child to shrink below its content width. */
.smart-review-page-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Keep the status icon pinned right and prevent it from being squeezed. */
.smart-review-page-status {
  flex: none;
  margin-left: auto;
}
</style>
